import { compact, uniq } from "lodash-es";

import {
  FORMAT_CATEGORIES_WITH_OTHER,
  FORMAT_GROUPS,
  type FormatCategoryWithOther,
} from "./formatGroups";

const VINYL_SET = new Set<string>(FORMAT_GROUPS.vinyl);
const CD_SET = new Set<string>(FORMAT_GROUPS.cd);
const CASSETTE_SET = new Set<string>(FORMAT_GROUPS.cassette);
const VIDEO_SET = new Set<string>(FORMAT_GROUPS.video);

const DIGITAL_FORMATS = new Set(["digital_media", "file"]);

const FORMAT_CATEGORY_SET = new Set<string>(FORMAT_CATEGORIES_WITH_OTHER);

/** Categorize a single normalized format key into a format category. */
export function categorizeFormat(
  format: string | null | undefined,
): FormatCategoryWithOther | null {
  if (!format) return "other";
  if (DIGITAL_FORMATS.has(format)) return null;
  if (VINYL_SET.has(format)) return "vinyl";
  if (CD_SET.has(format)) return "cd";
  if (CASSETTE_SET.has(format)) return "cassette";
  if (VIDEO_SET.has(format)) return "video";
  return "other";
}

/** Categorize format keys into unique format categories. Filters out digital. */
export function categorizeFormats(
  formats: readonly (string | null | undefined)[] | null | undefined,
): FormatCategoryWithOther[] {
  return uniq(compact((formats ?? []).map(categorizeFormat)));
}

export function normalizeFormatCategories(
  categories: readonly (string | null | undefined)[] | null | undefined,
): FormatCategoryWithOther[] {
  return uniq(
    compact(categories ?? []).filter((category): category is FormatCategoryWithOther =>
      FORMAT_CATEGORY_SET.has(category),
    ),
  );
}

export function resolveFormatCategories(input: {
  mediaCategories?: readonly (string | null | undefined)[] | null;
  mediaFormats?: readonly (string | null | undefined)[] | null;
  fallback?: FormatCategoryWithOther[];
}): FormatCategoryWithOther[] {
  const categories = normalizeFormatCategories(input.mediaCategories);
  if (categories.length > 0) return categories;

  const derivedCategories = categorizeFormats(input.mediaFormats);
  if (derivedCategories.length > 0) return derivedCategories;

  return input.fallback ?? [];
}

export function getPrimaryFormatCategory(input: {
  mediaCategories?: readonly (string | null | undefined)[] | null;
  mediaFormats?: readonly (string | null | undefined)[] | null;
  fallback?: FormatCategoryWithOther | null;
}): FormatCategoryWithOther | null {
  const categories = resolveFormatCategories({
    mediaCategories: input.mediaCategories,
    mediaFormats: input.mediaFormats,
    fallback: input.fallback ? [input.fallback] : [],
  });

  return FORMAT_CATEGORIES_WITH_OTHER.find((category) => categories.includes(category)) ?? null;
}

export function pickFormatCategoryByPriority(
  counts: Partial<Record<FormatCategoryWithOther, number>> | null | undefined,
): FormatCategoryWithOther | "all" {
  return FORMAT_CATEGORIES_WITH_OTHER.find((category) => (counts?.[category] ?? 0) > 0) ?? "all";
}

/** Check if all formats are digital (should be excluded from physical views). */
export function isDigitalOnly(formats: string[]): boolean {
  return formats.length > 0 && formats.every((f) => DIGITAL_FORMATS.has(f));
}

/** Check if a single format key is digital. */
export function isDigitalFormat(format: string): boolean {
  return DIGITAL_FORMATS.has(format);
}

/**
 * Get sort priority for a normalized format key.
 * Lower = higher priority: Vinyl (0) > CD (1) > Cassette (2) > Video (3) > Other (4).
 */
export function getFormatPriority(format: string | null): number {
  if (!format) return 4;
  if (VINYL_SET.has(format)) return 0;
  if (CD_SET.has(format)) return 1;
  if (CASSETTE_SET.has(format)) return 2;
  if (VIDEO_SET.has(format)) return 3;
  return 4;
}

/**
 * Get counts per category from a list of single format keys.
 * Used by list/collection hooks where format is a single DB value.
 */
export function getFormatCounts(
  formats: (string | null)[],
): Record<FormatCategoryWithOther | "all", number> {
  const counts: Record<FormatCategoryWithOther | "all", number> = {
    all: 0,
    vinyl: 0,
    cd: 0,
    cassette: 0,
    video: 0,
    other: 0,
  };

  for (const format of formats) {
    const category = categorizeFormat(format);
    if (category !== null) {
      counts[category]++;
      counts.all++;
    }
  }

  return counts;
}

/** Check if a format matches a given category filter. */
export function matchesFormatCategory(
  format: string | null,
  category: FormatCategoryWithOther,
): boolean {
  return categorizeFormat(format) === category;
}
